import { useEffect, useState } from "react";
import "./TemplatesTab.css";
import { BRANDING } from "../config";
import TiptapEditor from "./TiptapEditor";
import apiService from "../services/apiService";

/* global Office */

const extractFirstName = (fullName = "") => {
  const cleanedName = fullName.replace(/<[^>]+>/g, "").trim();
  const firstWord = cleanedName.split(/\s+/)[0];
  if (!firstWord || firstWord.includes("@")) return "there";
  return firstWord;
};

const fillTemplate = (text, tokens) =>
  text.replace(/{{(.*?)}}/g, (_, key) => tokens[key.trim()] || "");

const textToHtml = (text) =>
  text.split(/\n+/).filter(p => p.trim()).map(p => `<p>${p.trim()}</p>`).join('');

/* ─── Icons ─── */
const DocumentIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M14 2H7a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7z" />
    <path d="M14 2v5h5" /><path d="M9 13h6" /><path d="M9 17h6" /><path d="M9 9h1" />
  </svg>
);

const SearchIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" />
  </svg>
);

const TrashIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
  </svg>
);

const TemplatesTab = ({ emailInfo, salesforceContext, sfAuthError }) => {
  // ─── State ───
  const [searchTerm, setSearchTerm] = useState("");
  const [actionStatus, setActionStatus] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [localTemplates, setLocalTemplates] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [viewMode, setViewMode] = useState("all"); // "all" | "agency" | "personal"

  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);

  const [editedSubject, setEditedSubject] = useState("");
  const [editedBody, setEditedBody] = useState("");
  const [newTemplateTitle, setNewTemplateTitle] = useState("");
  const [newTemplateDescription, setNewTemplateDescription] = useState("");
  const [newCategoryId, setNewCategoryId] = useState("");
  const [newTemplateType, setNewTemplateType] = useState("personal");
  const [isSaving, setIsSaving] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);
  const [isDeleting, setIsDeleting] = useState(null); // template id being deleted

  const userInfo = {
    email: Office.context?.mailbox?.userProfile?.emailAddress || "unknown@example.com",
    name: Office.context?.mailbox?.userProfile?.displayName || "Unknown User"
  };

  const searchValue = searchTerm.trim().toLowerCase();
  const contactName = emailInfo.from || salesforceContext?.accountName || salesforceContext?.contact || "there";
  const contactFirstName = extractFirstName(contactName);
  const agentSignature = BRANDING.teamSignature;

  const personalizedTokens = { agentSignature, contactFirstName, contactName };

  /* ─── Load templates, categories, and admin status ─── */
  useEffect(() => {
    const init = async () => {
      setIsLoading(true);
      try {
        // Restored real backend logic for template initialization
        const [templatesData, categoriesData, adminData] = await Promise.all([
          apiService.getTemplates(userInfo.email),
          apiService.getCategories(),
          apiService.checkAdmin(userInfo.email),
        ]);

        if (Array.isArray(templatesData)) setLocalTemplates(templatesData);
        if (Array.isArray(categoriesData)) setCategories(categoriesData);
        if (adminData) setIsAdmin(adminData.isAdmin || false);

        /*
        // Mock data bypass (Commented out)
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        const mockTemplates = [
          { id: 1, title: "Initial Introduction", description: "Standard intro for new clients", category: "General", templateType: "agency", subject: "Welcome to our Agency", body: "Dear {{contactFirstName}},\n\nWelcome to our agency! We are thrilled to have you.", ownerEmail: "admin@agency.com" },
          { id: 2, title: "Follow-up Call", description: "Follow up after a missed call", category: "Follow-up", templateType: "personal", subject: "Sorry we missed you", body: "Hi {{contactFirstName}},\n\nI tried calling you today. Please get back to me.", ownerEmail: userInfo.email }
        ];
        
        const mockCategories = [
          { id: "c1", name: "General" },
          { id: "c2", name: "Follow-up" },
          { id: "c3", name: "Claims" }
        ];

        setLocalTemplates(mockTemplates);
        setCategories(mockCategories);
        setIsAdmin(true); 
        */

      } catch (err) {
        console.error("Templates init failed:", err);
      } finally {
        setIsLoading(false);
      }
    };
    init();
  }, []);

  /* ─── Filtering ─── */
  const visibleTemplates = localTemplates.filter((t) => {
    // View mode filter
    if (viewMode === "agency" && t.templateType !== "agency") return false;
    if (viewMode === "personal" && t.templateType !== "personal") return false;

    // Category filter
    if (selectedCategory !== "All" && t.category !== selectedCategory) return false;

    // Search filter
    if (searchValue) {
      const searchText = [t.title, t.description, t.category, ...(t.tags || [])].join(" ").toLowerCase();
      if (!searchText.includes(searchValue)) return false;
    }
    return true;
  });

  // Unique category names from loaded templates
  const activeCategories = [...new Set(localTemplates.map(t => t.category).filter(Boolean))];

  /* ─── Toast auto-clear ─── */
  useEffect(() => {
    if (actionStatus) setActionStatus(null);
  }, [searchTerm]);

  useEffect(() => {
    if (!actionStatus) return undefined;
    const id = window.setTimeout(() => setActionStatus(null), 3000);
    return () => window.clearTimeout(id);
  }, [actionStatus]);

  /* ─── Handlers ─── */
  const handleSelectTemplate = (template) => {
    const renderedSubject = fillTemplate(template.subject, personalizedTokens);
    const renderedBody = fillTemplate(template.body, personalizedTokens);

    setSelectedTemplate(template);
    setEditedSubject(renderedSubject);
    setEditedBody(template.body.includes("<") ? renderedBody : textToHtml(renderedBody));
    setNewTemplateTitle(template.title);
    setNewTemplateDescription(template.description);
    setNewCategoryId(template.categoryId ?? "");
    setIsCreating(false);
  };

  const handleCreateNew = () => {
    setIsCreating(true);
    setSelectedTemplate(null);
    setNewTemplateTitle("");
    setNewTemplateDescription("");
    setEditedSubject("");
    setEditedBody("");
    setNewCategoryId(categories[0]?.id ?? "");
    setNewTemplateType("personal");
  };

  const canEditTemplate = (template) => {
    if (template.templateType === "agency") return isAdmin;
    return template.ownerEmail?.toLowerCase() === userInfo.email.toLowerCase();
  };

  const canDeleteTemplate = (template) => {
    if (template.templateType === "agency") return isAdmin;
    return template.ownerEmail?.toLowerCase() === userInfo.email.toLowerCase();
  };

  const handleSaveToLibrary = () => setIsConfirming(true);

  const confirmSave = async () => {
    setIsConfirming(false);
    setIsSaving(true);
    try {
      // Restored real backend logic for template saving
      let response;
      if (isCreating) {
        response = await apiService.createTemplate({
          title: newTemplateTitle,
          description: newTemplateDescription,
          subject: editedSubject,
          body: editedBody,
          templateType: newTemplateType,
          categoryId: newCategoryId || null,
          userEmail: userInfo.email,
          userName: userInfo.name,
        });
        if (response.success) {
          // Refresh templates from backend to get full data
          const refreshed = await apiService.getTemplates(userInfo.email);
          if (Array.isArray(refreshed)) setLocalTemplates(refreshed);
          setIsCreating(false);
          setSelectedTemplate(null);
        }
      } else {
        response = await apiService.updateTemplate(selectedTemplate.id, {
          subject: editedSubject,
          body: editedBody,
          title: newTemplateTitle,
          description: newTemplateDescription,
          categoryId: newCategoryId || null,
          userEmail: userInfo.email,
          userName: userInfo.name,
        });
        if (response.success) {
          const refreshed = await apiService.getTemplates(userInfo.email);
          if (Array.isArray(refreshed)) setLocalTemplates(refreshed);
          setSelectedTemplate(null);
        }
      }

      /*
      // Mock data bypass (Commented out)
      await new Promise(resolve => setTimeout(resolve, 1500));
      const response = { success: true, version: 1 };

      if (response.success) {
        setActionStatus({ type: "success", message: `MOCK: Library updated! ${response.version ? `(v${response.version})` : ""}` });
        
        // Mocking the update in local state instead of re-fetching
        if (isCreating) {
          const newT = {
            id: Date.now(),
            title: newTemplateTitle,
            description: newTemplateDescription,
            subject: editedSubject,
            body: editedBody,
            templateType: newTemplateType,
            category: categories.find(c => c.id === newCategoryId)?.name || "General",
            ownerEmail: userInfo.email
          };
          setLocalTemplates(prev => [...prev, newT]);
          setIsCreating(false);
        } else {
          setLocalTemplates(prev => prev.map(t => t.id === selectedTemplate.id ? {
            ...t,
            title: newTemplateTitle,
            description: newTemplateDescription,
            subject: editedSubject,
            body: editedBody,
            category: categories.find(c => c.id === newCategoryId)?.name || t.category
          } : t));
        }
        setSelectedTemplate(null);
      }
      */

      if (response.success) {
        setActionStatus({ type: "success", message: `Library updated! ${response.version ? `(v${response.version})` : ""}` });
      } else {
        setActionStatus({ type: "error", message: response.error || "Save failed." });
      }
    } catch (err) {
      console.error("Save failed:", err);
      setActionStatus({ type: "error", message: "Failed to connect to backend." });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteTemplate = async (template) => {
    setIsDeleting(template.id);
  };

  const confirmDelete = async () => {
    const templateId = isDeleting;
    setIsDeleting(null);
    try {
      // Restored real backend logic for template deletion
      const res = await apiService.deleteTemplate(templateId, userInfo.email);
      if (res.success) {
        setLocalTemplates(prev => prev.filter(t => t.id !== templateId));
        setActionStatus({ type: "success", message: "Template deleted." });
        if (selectedTemplate?.id === templateId) {
          setSelectedTemplate(null);
        }
      } else {
        setActionStatus({ type: "error", message: res.error || "Delete failed." });
      }

      /*
      // Mock data bypass (Commented out)
      await new Promise(resolve => setTimeout(resolve, 800));
      setLocalTemplates(prev => prev.filter(t => t.id !== templateId));
      setActionStatus({ type: "success", message: "MOCK: Template deleted." });
      if (selectedTemplate?.id === templateId) {
        setSelectedTemplate(null);
      }
      */

    } catch (err) {
      console.error("Delete failed:", err);
      setActionStatus({ type: "error", message: "Failed to delete template." });
    }
  };

  const handleUseTemplate = async () => {
    const htmlBody = editedBody;
    try {
      const item = Office.context?.mailbox?.item;
      if (item?.displayReplyForm) {
        item.displayReplyForm({ htmlBody });
        setActionStatus({ type: "success", message: "Opened a reply draft." });
        setSelectedTemplate(null);
        return;
      }
      if (item?.body?.setSelectedDataAsync) {
        item.body.setSelectedDataAsync(
          htmlBody,
          { coercionType: Office.CoercionType?.Html || "html" },
          (result) => {
            if (result.status === Office.AsyncResultStatus.Succeeded) {
              setActionStatus({ type: "success", message: "Inserted into draft." });
              setSelectedTemplate(null);
            } else {
              setActionStatus({ type: "error", message: "Insert failed." });
            }
          }
        );
        return;
      }
    } catch (error) {
      console.error("Action failed:", error);
    }
    // Fallback: copy to clipboard
    const plainTextBody = editedBody.replace(/<[^>]+>/g, "\n").trim();
    const textToCopy = [`Subject: ${editedSubject}`, plainTextBody].filter(Boolean).join("\n\n");
    if (navigator?.clipboard?.writeText) {
      try {
        await navigator.clipboard.writeText(textToCopy);
        setActionStatus({ type: "success", message: "Copied to clipboard." });
        setSelectedTemplate(null);
      } catch (e) {
        console.error("Clipboard copy failed:", e);
      }
    }
  };

  /* ─── RENDER: List View ─── */
  const renderListView = () => (
    <>
      <div className="card-header templates-card-header">
        <div className="templates-card-header-main">
          <span className="card-header-icon templates-header-icon"><DocumentIcon /></span>
          <div className="templates-header-copy">
            <h3 className="card-header-title">Quick Templates</h3>
            <p className="templates-card-subtitle">
              {sfAuthError ? "⚠️ Salesforce restricted." : "Select a template to preview and use."}
            </p>
          </div>
        </div>
        <button className="add-template-btn" onClick={handleCreateNew} title="Create New Template">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
          </svg>
        </button>
      </div>

      <div className="card-body templates-card-body">
        {isLoading ? (
          <div className="section-loading" style={{ padding: '20px', textAlign: 'center' }}>
            <span className="spinner" /> Loading Library...
          </div>
        ) : (
          <>
            {/* View Mode Toggle */}
            <div className="templates-view-toggle">
              {["all", "agency", "personal"].map((mode) => (
                <button
                  key={mode}
                  className={`view-toggle-btn ${viewMode === mode ? "view-toggle-btn--active" : ""}`}
                  onClick={() => setViewMode(mode)}
                >
                  {mode === "all" ? "All" : mode === "agency" ? "Agency" : "Personal"}
                </button>
              ))}
            </div>

            {/* Category Filter Pills */}
            {activeCategories.length > 1 && (
              <div className="category-pills">
                <button
                  className={`category-pill ${selectedCategory === "All" ? "category-pill--active" : ""}`}
                  onClick={() => setSelectedCategory("All")}
                >
                  All
                </button>
                {activeCategories.map((cat) => (
                  <button
                    key={cat}
                    className={`category-pill ${selectedCategory === cat ? "category-pill--active" : ""}`}
                    onClick={() => setSelectedCategory(cat)}
                  >
                    {cat}
                  </button>
                ))}
              </div>
            )}

            {/* Search */}
            <label className="templates-search" htmlFor="template-search">
              <span className="templates-search-icon"><SearchIcon /></span>
              <input
                id="template-search"
                type="search"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search templates..."
              />
            </label>

            {/* Template List */}
            {visibleTemplates.length > 0 ? (
              <div className="templates-list">
                {visibleTemplates.map((template) => (
                  <div key={template.id} className="template-list-item-wrapper">
                    <button
                      type="button"
                      className="template-list-item"
                      onClick={() => handleSelectTemplate(template)}
                    >
                      <span className="template-list-item-icon"><DocumentIcon /></span>
                      <span className="template-list-item-copy">
                        <span className="template-list-item-title-row">
                          <span className="template-list-item-title">{template.title}</span>
                          <span className={`type-badge type-badge--${template.templateType}`}>
                            {template.templateType === "agency" ? "Agency" : "Personal"}
                          </span>
                        </span>
                        <span className="template-list-item-description">{template.description}</span>
                        <span className="template-list-item-meta">
                          {template.category && <span className="meta-category">{template.category}</span>}
                        </span>
                      </span>
                    </button>
                    {canDeleteTemplate(template) && (
                      <button
                        className="template-delete-btn"
                        onClick={(e) => { e.stopPropagation(); handleDeleteTemplate(template); }}
                        title="Delete template"
                      >
                        <TrashIcon />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="templates-empty-state">
                <p>No templates found.</p>
                <span>Try a different search or filter.</span>
              </div>
            )}
          </>
        )}
      </div>
    </>
  );

  /* ─── RENDER: Preview / Edit View ─── */
  const renderEditView = () => {
    const isAgency = selectedTemplate?.templateType === "agency";
    const canEdit = isCreating || canEditTemplate(selectedTemplate);
    const isPreviewOnly = !isCreating && !canEdit;
    const showTypeSelector = isCreating && isAdmin;

    return (
      <div className="card-body">
        <div className="template-preview-overlay">
          <div className="preview-header">
            <button className="back-btn" onClick={() => { setSelectedTemplate(null); setIsCreating(false); }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="19" y1="12" x2="5" y2="12" /><polyline points="12 19 5 12 12 5" />
              </svg>
            </button>
            <span className="preview-title">
              {isCreating ? "New Template" : selectedTemplate.title}
            </span>
            {!isCreating && (
              <span className={`type-badge type-badge--${selectedTemplate.templateType}`}>
                {isAgency ? "Agency" : "Personal"}
              </span>
            )}
          </div>

          <div className="preview-editor">
            {/* Title & Description — shown for new templates and editable templates */}
            {(isCreating || canEdit) && (
              <>
                <div className="editor-field">
                  <label className="editor-label">Template Title</label>
                  <input
                    className="editor-input"
                    value={newTemplateTitle}
                    onChange={(e) => setNewTemplateTitle(e.target.value)}
                    placeholder="e.g. Follow-up"
                    readOnly={isPreviewOnly}
                  />
                </div>
                <div className="editor-field">
                  <label className="editor-label">Short Description</label>
                  <input
                    className="editor-input"
                    value={newTemplateDescription}
                    onChange={(e) => setNewTemplateDescription(e.target.value)}
                    placeholder="Brief summary"
                    readOnly={isPreviewOnly}
                  />
                </div>
              </>
            )}

            {/* Category Dropdown */}
            {(isCreating || canEdit) && categories.length > 0 && (
              <div className="editor-field">
                <label className="editor-label">Category</label>
                <select
                  className="editor-input editor-select"
                  value={newCategoryId}
                  onChange={(e) => setNewCategoryId(e.target.value)}
                  disabled={isPreviewOnly}
                >
                  <option value="">Select category...</option>
                  {categories.map((cat) => (
                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                  ))}
                </select>
              </div>
            )}

            {/* Template Type Selector — only for admins creating new templates */}
            {showTypeSelector && (
              <div className="editor-field">
                <label className="editor-label">Template Type</label>
                <div className="type-selector">
                  <label className={`type-option ${newTemplateType === "personal" ? "type-option--active" : ""}`}>
                    <input
                      type="radio"
                      name="templateType"
                      value="personal"
                      checked={newTemplateType === "personal"}
                      onChange={() => setNewTemplateType("personal")}
                    />
                    <span>Personal</span>
                  </label>
                  <label className={`type-option ${newTemplateType === "agency" ? "type-option--active" : ""}`}>
                    <input
                      type="radio"
                      name="templateType"
                      value="agency"
                      checked={newTemplateType === "agency"}
                      onChange={() => setNewTemplateType("agency")}
                    />
                    <span>Agency</span>
                  </label>
                </div>
              </div>
            )}

            {/* Subject */}
            <div className="editor-field">
              <label className="editor-label">Subject</label>
              <input
                className="editor-input"
                value={editedSubject}
                onChange={(e) => setEditedSubject(e.target.value)}
                readOnly={isPreviewOnly}
              />
            </div>

            {/* Body */}
            <div className="editor-field">
              <label className="editor-label">Body {isPreviewOnly ? "(Preview)" : "(Edit)"}</label>
              <TiptapEditor content={editedBody} onChange={setEditedBody} />
            </div>
          </div>

          <div className="preview-actions">
            {canEdit && (
              <button className="btn-secondary" onClick={handleSaveToLibrary} disabled={isSaving}>
                {isSaving ? "Saving..." : (isCreating ? "Save to Library" : "Save Changes")}
              </button>
            )}
            {!isCreating && (
              <button className="btn-primary" onClick={handleUseTemplate}>
                Insert &amp; Send
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  /* ─── Main Render ─── */
  return (
    <div className="templates-tab">
      {actionStatus && (
        <div className={`template-toast template-toast--${actionStatus.type}`} role="alert">
          {actionStatus.message}
        </div>
      )}

      <div className="card">
        {!selectedTemplate && !isCreating ? renderListView() : renderEditView()}
      </div>

      {/* Save Confirmation Modal */}
      {isConfirming && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>{isCreating ? "Create Template" : "Confirm Changes"}</h3>
            </div>
            <div className="modal-body">
              <p>
                {isCreating
                  ? `Create "${newTemplateTitle}" as a ${newTemplateType} template?`
                  : `Save changes to "${selectedTemplate?.title}"?`}
                {" "}This will be tracked to <strong>{userInfo.name}</strong> ({userInfo.email}).
              </p>
            </div>
            <div className="modal-footer">
              <button className="btn-cancel" onClick={() => setIsConfirming(false)}>Cancel</button>
              <button className="btn-confirm" onClick={confirmSave}>
                {isCreating ? "Create" : "Save Changes"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {isDeleting && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Delete Template</h3>
            </div>
            <div className="modal-body">
              <p>Are you sure you want to delete this template? This action cannot be undone.</p>
            </div>
            <div className="modal-footer">
              <button className="btn-cancel" onClick={() => setIsDeleting(null)}>Cancel</button>
              <button className="btn-confirm btn-confirm--danger" onClick={confirmDelete}>Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TemplatesTab;
